import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { withReturnTo } from '../common/returnPath';
import api from '../../services/api';
import SeedFromRoom from '../subjects/SeedFromRoom';
import { useProject } from '../../contexts/ProjectContext';
import { useTopicWorkspace } from '../../contexts/TopicWorkspaceContext';
import { useKeywordClipboard } from '../../contexts/KeywordClipboardContext';
import { useWebSocket } from '../../contexts/WebSocketContext';
import { useNetworkEnabled } from '../../hooks/useNetworkEnabled';
import { LineChart, Line, Tooltip } from 'recharts';
import type { TopicKeyword, AliasedChild, DeploymentPlan, PageStyle, ManagedSite, WebSocketEvent, MonthlyDataPoint } from '../../types';
import SerpModal from '../SerpModal';
import Modal from '../Modal';
import { SitePlanChip } from '../common/SitePlanChip';
import {
  RUN_FORMATS,
  runButtonLabel,
  runOutcome,
  runTitle,
} from '../common/runFormats';
import { EngineIcon, ExternalSearchLink, RefreshGlyph, formatSerpDate } from '../common/SerpHelpers';
import {NULL_REF_ID} from '../../services/api';
import MakePagesDialog from '../common/MakePagesDialog';
import type { MakePagesResult } from '../common/MakePagesDialog';
import { useInfiniteScroll, LoadingMore, EndOfList } from '../../hooks/useInfiniteScroll';
import {
  EMPTY_GAP_FILTER,
  SerpGapBadge,
  SerpGapFilterFields,
  gapFilterActive,
  gapFilterLabel,
} from './SerpGap';
import type { SerpGapFilter } from '../../types';
import { BULK_FETCH_FRESH_DAYS, BULK_FETCH_MAX, keywordsToFetch } from './bulkSerpFetch';
import SerpFailedNote from './SerpFailedNote';

// ============= Cluster helpers (derived from alias_parent) =============

type KeywordRow =
  | { type: 'unclustered'; keyword: TopicKeyword }
  | { type: 'cluster_header'; parent: TopicKeyword; searchSum: number };

const WINNABLE_HELP =
  'Show only ★ keywords: the right size, not heavily contested, and with room on ' +
  'page one (20–1,000 searches a month and 1 open result, unless you changed it in ' +
  'Settings → Keywords). Pages aimed at small terms reach the first page far more ' +
  'often than pages aimed at big ones - and position 11 gets almost no clicks.';

/** The ★ beside a keyword a new page can realistically rank for. The hover
 *  says why — or, for a near miss, nothing: a star that explains its absence
 *  on every row would be noise. */
function WinnableStar({ kw }: { kw: Pick<TopicKeyword, 'winnable' | 'winnable_reason'> }) {
  if (!kw.winnable) return null;
  return (
    <span
      className="ml-1 text-amber-500 text-xs cursor-help"
      title={`Winnable: ${kw.winnable_reason ?? 'a small, not heavily contested term.'}`}
      aria-label="Winnable keyword"
    >
      ★
    </span>
  );
}

function buildKeywordRows(
  keywords: TopicKeyword[],
): KeywordRow[] {
  const rows: KeywordRow[] = [];

  for (const kw of keywords) {
    if (kw.alias_parent_id) continue; // child — shown under parent via aliased_children
    if (kw.aliased_children && kw.aliased_children.length > 0) {
      const searchSum = (kw.avg_monthly_searches || 0) +
        kw.aliased_children.reduce((s, c) => s + (c.avg_monthly_searches || 0), 0);
      rows.push({ type: 'cluster_header', parent: kw, searchSum });
    } else {
      rows.push({ type: 'unclustered', keyword: kw });
    }
  }

  return rows;
}



const PAGE_STYLES: { value: PageStyle; label: string }[] = [
  { value: 'ARTICLE', label: 'Article' },
  { value: 'GUIDE', label: 'Guide' },
  { value: 'CHECKLIST', label: 'Checklist' },
  { value: 'LISTICLE', label: 'Listicle' },
  { value: 'FAQ', label: 'FAQ' },
  { value: 'COMPARISON', label: 'Comparison' },
  { value: 'REVIEWS', label: 'Reviews' },
  { value: 'PRODUCT_PAGE', label: 'Product Page' },
  { value: 'PRICING_PAGE', label: 'Pricing Page' },
  { value: 'SUPPORT_ARTICLE', label: 'Support Article' },
  { value: 'DOCUMENTATION', label: 'Documentation' },
  { value: 'TOOL', label: 'Tool' },
  { value: 'FORUM_DISCUSSION', label: 'Forum Discussion' },
  { value: 'VIDEO', label: 'Video' },
  { value: 'TEMPLATE', label: 'Template' },
];

const PAGE_SIZE = 50;
const DEFAULT_MIN_SEM_SIMILARITY = 0.8;

type SortColumn = 'keyword' | 'relevance' | 'avg_monthly_searches' | 'semantic' | 'cluster_size' | 'has_page' | 'latest_serp_date';

interface LinkedKeywordsPaneProps {
  onDiscoverClick?: () => void;
}

export default function LinkedKeywordsPane({ onDiscoverClick }: LinkedKeywordsPaneProps) {
  const navigate = useNavigate();
  const here = useLocation();
  const [searchParams] = useSearchParams();
  const { selectedTopic, selectedProjectId, topics } = useProject();
  const { clipboard, cut, copy, clear: clearClipboard } = useKeywordClipboard();
  const highlightKeywordIdParam = searchParams.get('highlight_keyword') ? Number(searchParams.get('highlight_keyword')) : null;
  const [highlightKeywordIds, setHighlightKeywordIds] = useState<Set<number>>(
    highlightKeywordIdParam ? new Set([highlightKeywordIdParam]) : new Set()
  );
  const {
    linkedKeywordsVersion,
    triggerLinkedRefresh,
    regrouping,
    setRegrouping,
  } = useTopicWorkspace();
  const { subscribe } = useWebSocket();
  const networkEnabled = useNetworkEnabled();

  const [keywords, setKeywords] = useState<TopicKeyword[]>([]);
  // Only offered when a room is actually feeding this subject — a button that
  // opens a dialog saying "nothing is linked" is a button that wasted a click.
  const [linkedRoom, setLinkedRoom] = useState<{ name: string } | null>(null);
  const [seedOpen, setSeedOpen] = useState(false);
  const [loadingMonthly, setLoadingMonthly] = useState(false);
  const [monthlyDataMap, setMonthlyDataMap] = useState<Map<number, MonthlyDataPoint[]>>(new Map());
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [sortBy, setSortBy] = useState<SortColumn>('keyword');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [allMatchingSelected, setAllMatchingSelected] = useState(false);
  const [bulkRemoving, setBulkRemoving] = useState(false);
  const [pasting, setPasting] = useState(false);

  // Cluster state (derived from alias_parent on keywords)
  const [, setDissolvingCluster] = useState(false);
  const [creatingCluster, setCreatingCluster] = useState(false);
  const [expandedClusterIds, setExpandedClusterIds] = useState<Set<number>>(new Set());

  // Ref to track if this is an initial load or loading more
  const isInitialLoad = useRef(true);

  // Filter state
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [textFilter, setTextFilter] = useState('');
  const [appliedTextFilter, setAppliedTextFilter] = useState('');
  // "★ only": applied immediately, no Apply button — it's a toggle, not a query.
  const [winnableOnly, setWinnableOnly] = useState(false);
  // The top-10 gap filter: edited in the panel, applied with Apply Filters.
  const [gapDraft, setGapDraft] = useState<SerpGapFilter>(EMPTY_GAP_FILTER);
  const [appliedGap, setAppliedGap] = useState<SerpGapFilter>(EMPTY_GAP_FILTER);
  const [semanticQuery, setSemanticQuery] = useState('');
  const [appliedSemanticQuery, setAppliedSemanticQuery] = useState('');
  const [minSemanticSimilarity, setMinSemanticSimilarity] = useState(0.8);
  const [appliedMinSemanticSimilarity, setAppliedMinSemanticSimilarity] = useState(0.8);

  // SERP modal state
  const [serpModalKeyword, setSerpModalKeyword] = useState<{ id: number; value: string } | null>(null);

  // SERP request state
  const [requestingSerpFor, setRequestingSerpFor] = useState<number | null>(null);
  const [requestingBulkSerpFor, setRequestingBulkSerpFor] = useState<number | null>(null);

  // Create Pages modal state
  const [createPagesModalOpen, setCreatePagesModalOpen] = useState(false);
  // '' = no override; the Angle phase picks each page's style from its own
  // SERP. Default, because at create time nobody has looked at the SERP yet —
  // a style chosen here is a guess, and it used to silently become a pin.
  const [createPageTkIds, setCreatePageTkIds] = useState<number[]>([]);
  // How many things this run is over. "All matching" means the server-side
  // total, not the rows that happen to be loaded — the same distinction
  // useRowSelection exists to keep honest.
  const selectedForRun = allMatchingSelected ? totalCount : createPageTkIds.length;

  // Inline creation state for managed site / deployment plan

  const keywordRows = useMemo(
  () => buildKeywordRows(keywords),
  [keywords, sortBy, sortOrder],
);

  const hasActiveFilters =
    appliedTextFilter || appliedSemanticQuery || winnableOnly || gapFilterActive(appliedGap);

  const fetchKeywords = useCallback(async (pageNum: number, append: boolean = false, silent: boolean = false) => {
    if (!selectedTopic) {
      setKeywords([]);
      return;
    }

    if (append) {
      setLoadingMore(true);
    } else if (!silent) {
      // Hard refresh (filter/sort/topic change): clear immediately so stale
      // rows don't linger while the new set loads.
      setLoading(true);
      setKeywords([]);  // Clear old data immediately
      setMonthlyDataMap(new Map());  // Clear sparkline data with keywords
      setHasMore(false);  // Prevent loadMore while refreshing
      isInitialLoad.current = true;
    }
    // Silent refresh (background WS event): keep current rows on screen and swap
    // in the new data when it arrives — no blank flash, changed rows update in
    // place. Selection + sparklines are preserved (merged) below.

    try {
      // When semantic query is active, sort by semantic similarity
      const effectiveSortBy = appliedSemanticQuery ? 'semantic' : sortBy;

      const data = await api.getTopicKeywords(
        selectedTopic.id,
        null,
        pageNum,
        PAGE_SIZE,
        effectiveSortBy === 'semantic' ? 'similarity' : effectiveSortBy,
        sortOrder,
        false,
        false,
        appliedTextFilter || null,
        appliedSemanticQuery || null,
        null,
        appliedSemanticQuery ? appliedMinSemanticSimilarity : null,
        false, // includeMonthly — load fast first, sparklines come separately
        winnableOnly,
        appliedGap,
      );

      if (append) {
        setKeywords(prev => {
          const existingIds = new Set(prev.map(k => k.id));
          const newItems = data.items.filter(k => !existingIds.has(k.id));
          return [...prev, ...newItems];
        });
      } else {
        setKeywords(data.items);
        if (!silent) {
          // Preserve the user's selection across a background refresh.
          setSelectedIds(new Set());
          setAllMatchingSelected(false);
        }
      }
      setHasMore(pageNum < data.pages);
      setTotalCount(data.total);

      // Lazy-load monthly data for sparklines into a separate map —
      // keywords state is never touched, so the list doesn't re-render.
      const allIds = data.items.flatMap(kw => [
        kw.keyword_id,
        ...kw.aliased_children.map(c => c.keyword_id),
      ]);
      if (allIds.length > 0) {
        setLoadingMonthly(true);
        api.getTopicKeywordsMonthlyData(allIds).then(monthlyResults => {
          setMonthlyDataMap(prev => {
            const next = (append || silent) ? new Map(prev) : new Map();
            monthlyResults.forEach(r => next.set(r.keyword_id, r.monthly_data));
            return next;
          });
        }).catch(err => {
          console.error('Failed to fetch monthly data:', err);
        }).finally(() => {
          setLoadingMonthly(false);
        });
      }
    } catch (err) {
      console.error('Failed to fetch keywords:', err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
      isInitialLoad.current = false;
    }
  }, [selectedTopic, sortBy, sortOrder, appliedTextFilter, appliedSemanticQuery, appliedMinSemanticSimilarity, winnableOnly, appliedGap]);

  // Debounced full-list reload, for high-frequency WebSocket events. A batch of
  // browser-plugin SERP completions publishes one serp_completion per keyword;
  // without coalescing each would clear + refetch the whole list (a visible
  // "refreshing" flicker). Trailing 400ms debounce + 1500ms max-wait so a steady
  // stream still repaints periodically. Mirrors DiscoveryPane's scheduleFetchIdeas.
  const fetchKeywordsRef = useRef(fetchKeywords);
  useEffect(() => {
    fetchKeywordsRef.current = fetchKeywords;
  }, [fetchKeywords]);
  const fetchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fetchMaxWaitRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scheduleFetchKeywords = useCallback(() => {
    const run = () => {
      if (fetchDebounceRef.current) {
        clearTimeout(fetchDebounceRef.current);
        fetchDebounceRef.current = null;
      }
      if (fetchMaxWaitRef.current) {
        clearTimeout(fetchMaxWaitRef.current);
        fetchMaxWaitRef.current = null;
      }
      fetchKeywordsRef.current(1, false, true); // silent: refresh in place, no blank flash
    };
    if (fetchDebounceRef.current) clearTimeout(fetchDebounceRef.current);
    fetchDebounceRef.current = setTimeout(run, 400);
    if (!fetchMaxWaitRef.current) fetchMaxWaitRef.current = setTimeout(run, 1500);
  }, []);
  useEffect(
    () => () => {
      if (fetchDebounceRef.current) clearTimeout(fetchDebounceRef.current);
      if (fetchMaxWaitRef.current) clearTimeout(fetchMaxWaitRef.current);
    },
    []
  );

  // Initial load and reload when filters/sort/topic changes
  useEffect(() => {
    setPage(1);
    fetchKeywords(1, false);
  }, [fetchKeywords, linkedKeywordsVersion]);

  // Listen for SERP and relevance score events
  useEffect(() => {
    return subscribe((event: WebSocketEvent) => {
      if (event.event_type === 'serp_requested') {
        console.log('[WS] serp_requested received:', event.keyword_ids);
        const ids = new Set(event.keyword_ids);
        setKeywords(prev => prev.map(kw => {
          const matchesParent = ids.has(kw.keyword_id);
          const matchesChild = kw.aliased_children?.some(c => ids.has(c.keyword_id));
          if (!matchesParent && !matchesChild) return kw;
          return {
            ...kw,
            has_pending_serp_request: matchesParent ? true : kw.has_pending_serp_request,
            aliased_children: kw.aliased_children?.map(c =>
              ids.has(c.keyword_id) ? { ...c, has_pending_serp_request: true } : c
            ),
          };
        }));
      } else if (event.event_type === 'serp_completion') {
        console.log('[WS] serp_completion received:', event.keyword_ids);
        scheduleFetchKeywords();
      } else if (event.event_type === 'relevance_score') {
        console.log('[WS] relevance_score received:', event.topic_keyword_ids);
        scheduleFetchKeywords();
      }
    });
  }, [subscribe, scheduleFetchKeywords]);

  // Separately-fetched list of ALL cluster parents (unaffected by filters)
  // Used for the "Add to alias cluster..." dropdown
  const [allClusterParents, setAllClusterParents] = useState<TopicKeyword[]>([]);

  const fetchClusterParents = useCallback(async () => {
    if (!selectedTopic) {
      setAllClusterParents([]);
      return;
    }
    try {
      // Unfiltered fetch to find all keywords that are cluster parents
      // const data = await api.getTopicKeywords(selectedTopic.id, null, 1, 100, 'keyword', 'asc');
      const data = await api.getTopicKeywordAliasClusters(selectedTopic.id, 1, 100);

      setAllClusterParents(data.items.filter(kw => kw.aliased_children && kw.aliased_children.length > 0));
    } catch (err) {
      console.error('Failed to fetch cluster parents:', err);
    }
  }, [selectedTopic]);

  useEffect(() => {
    fetchClusterParents();
  }, [fetchClusterParents, linkedKeywordsVersion]);

  useEffect(() => {
    if (!selectedProjectId) {
      setLinkedRoom(null);
      return;
    }
    api
      .getRoomForSubject(selectedProjectId)
      .then((r) => setLinkedRoom(r ? { name: r.name } : null))
      // A subject with no room is the common case and not worth an error.
      .catch(() => setLinkedRoom(null));
  }, [selectedProjectId]);

  /**
   * Refresh when a regroup finishes, wherever it was started.
   *
   * One button starts one — "Regroup terms" below — and this list is stale the
   * moment it lands, because regrouping decides which terms share a page.
   *
   * No message here. The workspace owns both the running chip and the failure
   * text, because only it is mounted on every tab; a run started here and read
   * on Plan would otherwise report nothing.
   */
  useEffect(() => {
    return subscribe((event: WebSocketEvent) => {
      if (event.event_type !== 'terms_regrouped') return;
      if (selectedTopic && event.topic_id !== selectedTopic.id) return;
      if (event.status !== 'done') return;
      fetchKeywords(1, false);
      fetchClusterParents();
    });
  }, [subscribe, fetchKeywords, fetchClusterParents, selectedTopic]);

  // Clear filters when topic changes
  useEffect(() => {
    setPage(1);
    setTextFilter('');
    setAppliedTextFilter('');
    setSemanticQuery('');
    setAppliedSemanticQuery('');
    setMinSemanticSimilarity(DEFAULT_MIN_SEM_SIMILARITY);
    setAppliedMinSemanticSimilarity(DEFAULT_MIN_SEM_SIMILARITY);
  }, [selectedTopic]);

  // Scroll to and highlight keywords
  useEffect(() => {
    if (highlightKeywordIds.size === 0 || loading) return;
    // Scroll to the first highlighted keyword
    const firstId = highlightKeywordIds.values().next().value;
    const el = document.querySelector(`[data-keyword-id="${firstId}"]`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    // Fade highlight after 3 seconds
    const timer = setTimeout(() => {
      setHighlightKeywordIds(new Set());
    }, 3000);
    return () => clearTimeout(timer);
  }, [highlightKeywordIds, loading, keywords]);

  // Load more function for infinite scroll
  const loadMore = useCallback(() => {
    // Don't load more while a fresh fetch is in progress (e.g., sort/filter change)
    if (isInitialLoad.current) return;
    if (!loadingMore && hasMore) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchKeywords(nextPage, true);
    }
  }, [loadingMore, hasMore, page, fetchKeywords]);

  // Infinite scroll hook
  const scrollContainerRef = useInfiniteScroll({
    hasMore,
    isLoading: loading || loadingMore,
    onLoadMore: loadMore,
    threshold: 300,
  });

  const handleSort = (column: SortColumn) => {
    // Scroll to top immediately when sort changes
    scrollContainerRef.current?.scrollTo(0, 0);

    // When in semantic mode, clicking relevance toggles the similarity sort order
    if (appliedSemanticQuery && column === 'relevance') {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
      return;
    }
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder(column === 'keyword' ? 'asc' : 'desc');
    }
    // Page reset and data fetch happens automatically via useEffect when sort changes
  };

  const handleApplyFilters = () => {
    // Scroll to top when filters change
    scrollContainerRef.current?.scrollTo(0, 0);
    setAppliedTextFilter(textFilter);
    setAppliedSemanticQuery(semanticQuery);
    setAppliedMinSemanticSimilarity(minSemanticSimilarity);
    setAppliedGap(gapDraft);
    // Page reset happens automatically via useEffect when filters change
  };

  const handleClearFilters = () => {
    // Scroll to top when filters change
    scrollContainerRef.current?.scrollTo(0, 0);
    setTextFilter('');
    setAppliedTextFilter('');
    setSemanticQuery('');
    setAppliedSemanticQuery('');
    setMinSemanticSimilarity(DEFAULT_MIN_SEM_SIMILARITY);
    setAppliedMinSemanticSimilarity(DEFAULT_MIN_SEM_SIMILARITY);
    setWinnableOnly(false);
    setGapDraft(EMPTY_GAP_FILTER);
    setAppliedGap(EMPTY_GAP_FILTER);
    // Page reset happens automatically via useEffect when filters change
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      // Only select unclustered keywords for "select all"
      const unclusteredIds = keywords
        .filter(k => !k.alias_parent_id && !(k.aliased_children && k.aliased_children.length > 0))
        .map(k => k.id);
      setSelectedIds(new Set(unclusteredIds));
    } else {
      setSelectedIds(new Set());
      setAllMatchingSelected(false);
    }
  };

  const handleSelectOne = (id: number, checked: boolean) => {
    setAllMatchingSelected(false);
    const newSelected = new Set(selectedIds);
    if (checked) {
      newSelected.add(id);
    } else {
      newSelected.delete(id);
    }
    setSelectedIds(newSelected);
  };

  const handleSelectAllMatching = () => {
    setSelectedIds(new Set(keywords.map((k) => k.id)));
    setAllMatchingSelected(true);
  };

  const getSelectedCount = () => {
    return allMatchingSelected ? totalCount : selectedIds.size;
  };

  // The selected rows' keywords that a "Fetch SERPs" press would search.
  const serpFetchIds = useMemo(
    () => keywordsToFetch(keywords.filter((k) => selectedIds.has(k.id))),
    [keywords, selectedIds],
  );
  const [fetchingSerps, setFetchingSerps] = useState(false);

  const handleFetchSerps = async () => {
    if (!networkEnabled || serpFetchIds.length === 0) return;
    if (
      serpFetchIds.length > BULK_FETCH_MAX &&
      !window.confirm(
        `Search the first ${BULK_FETCH_MAX} of ${serpFetchIds.length} keywords? ` +
          'Press again afterwards for the rest.',
      )
    ) {
      return;
    }
    setFetchingSerps(true);
    try {
      const res = await api.createSerpRequestsBulk(serpFetchIds.slice(0, BULK_FETCH_MAX));
      const started = new Set(res.queued);
      setKeywords((prev) =>
        prev.map((kw) => (started.has(kw.keyword_id) ? { ...kw, has_pending_serp_request: true } : kw)),
      );
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to fetch SERPs');
    } finally {
      setFetchingSerps(false);
    }
  };

  const handleRequestSerp = async (keywordId: number) => {
    if (!networkEnabled) return;
    setRequestingSerpFor(keywordId);
    try {
      await api.createSerpRequest(keywordId);
      // Update the local state to show pending - could be a top-level keyword or a child
      setKeywords(prev => prev.map(kw => {
        if (kw.keyword_id === keywordId) {
          return { ...kw, has_pending_serp_request: true };
        }
        if (kw.aliased_children?.some(c => c.keyword_id === keywordId)) {
          return {
            ...kw,
            aliased_children: kw.aliased_children.map(c =>
              c.keyword_id === keywordId ? { ...c, has_pending_serp_request: true } : c
            ),
          };
        }
        return kw;
      }));
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to request SERP');
    } finally {
      setRequestingSerpFor(null);
    }
  };

  // Cancel a stuck/in-flight crawl: clear the pending flag locally right
  // away (the backend releases the lock + broadcasts serp_completion to
  // other open views). Works for a top-level keyword or an aliased child.
  const handleCancelSerp = async (keywordId: number) => {
    const clear = (kw: TopicKeyword): TopicKeyword => {
      if (kw.keyword_id === keywordId) {
        return { ...kw, has_pending_serp_request: false };
      }
      if (kw.aliased_children?.some(c => c.keyword_id === keywordId)) {
        return {
          ...kw,
          aliased_children: kw.aliased_children.map(c =>
            c.keyword_id === keywordId ? { ...c, has_pending_serp_request: false } : c
          ),
        };
      }
      return kw;
    };
    setKeywords(prev => prev.map(clear));
    try {
      await api.cancelSerpRequest(keywordId);
    } catch (err) {
      console.error('Failed to cancel SERP request', err);
    }
  };

  const handleBulkRequestSerp = async (parent: TopicKeyword) => {
    if (!networkEnabled) return;
    const allKeywordIds = [
      parent.keyword_id,
      ...parent.aliased_children.map(c => c.keyword_id),
    ];
    const toRequest = allKeywordIds.filter(kid => {
      if (kid === parent.keyword_id) {
        return !parent.has_pending_serp_request;
      }
      const child = parent.aliased_children.find(c => c.keyword_id === kid);
      return child && !child.has_pending_serp_request;
    });
    if (toRequest.length === 0) return;

    setRequestingBulkSerpFor(parent.id);
    try {
      await Promise.all(toRequest.map(kid => api.createSerpRequest(kid)));
      setKeywords(prev => prev.map(kw => {
        if (kw.id !== parent.id) return kw;
        return {
          ...kw,
          has_pending_serp_request: toRequest.includes(kw.keyword_id) ? true : kw.has_pending_serp_request,
          aliased_children: kw.aliased_children.map(c =>
            toRequest.includes(c.keyword_id) ? { ...c, has_pending_serp_request: true } : c
          ),
        };
      }));
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to request SERPs');
    } finally {
      setRequestingBulkSerpFor(null);
    }
  };

  const handleRemove = async (topicKeyword: TopicKeyword) => {
    if (!window.confirm('Remove this keyword from the topic?')) return;

    try {
      await api.deleteTopicKeyword(topicKeyword.id);
      triggerLinkedRefresh();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to unlink keyword');
    }
  };

  const handleBulkPin = async (pinned: boolean) => {
    if (selectedIds.size === 0) return;
    try {
      await api.bulkPinKeywords(Array.from(selectedIds), pinned);
      triggerLinkedRefresh();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to update pin status');
    }
  };

  // ---- Cut / copy / paste keywords between topics --------------------------
  // Cut/copy stash the selected topic_keyword ids on the shared clipboard;
  // paste commits them into the *current* topic. Cut is one-shot (commit on
  // paste clears the clipboard); copy persists so it can be pasted into
  // several topics.
  const handleCut = () => {
    if (selectedIds.size === 0 || !selectedTopic) return;
    cut(Array.from(selectedIds), selectedTopic.id);
    setSelectedIds(new Set());
    setAllMatchingSelected(false);
  };

  const handleCopy = () => {
    if (selectedIds.size === 0 || !selectedTopic) return;
    copy(Array.from(selectedIds), selectedTopic.id);
    setSelectedIds(new Set());
    setAllMatchingSelected(false);
  };

  const handlePaste = async () => {
    if (!clipboard || !selectedTopic || pasting) return;
    // Pasting into the same topic the keywords came from is a no-op.
    if (clipboard.sourceTopicId === selectedTopic.id) return;
    setPasting(true);
    try {
      const res = await api.bulkMoveKeywords(
        clipboard.ids,
        selectedTopic.id,
        clipboard.mode
      );
      // Paste dismisses the clipboard for both cut and copy — the pill goes
      // away and dimmed cut rows resolve. (Esc dismisses it without pasting.)
      clearClipboard();
      triggerLinkedRefresh();
      const done = clipboard.mode === 'cut' ? res.moved : res.copied;
      if (res.skipped > 0) {
        const verb = clipboard.mode === 'cut' ? 'moved' : 'copied';
        alert(
          `${done} keyword(s) ${verb}; ${res.skipped} skipped (already in this topic).`
        );
      }
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to paste keywords');
    } finally {
      setPasting(false);
    }
  };

  // A row is "cut" (shown dimmed) only while sitting in its source topic with
  // a pending cut on the clipboard.
  const isCutRow = (tkId: number) =>
    clipboard?.mode === 'cut' &&
    clipboard.sourceTopicId === selectedTopic?.id &&
    clipboard.ids.includes(tkId);

  const pasteTargetIsSource =
    !!clipboard && clipboard.sourceTopicId === selectedTopic?.id;
  const clipboardSourceName =
    clipboard &&
    (topics.find((t) => t.id === clipboard.sourceTopicId)?.name ??
      `topic #${clipboard.sourceTopicId}`);

  // Document-level shortcuts so paste works even when focus isn't in the pane
  // (e.g. right after navigating to the destination topic). Kept in a ref so
  // the listener binds once but always sees current state. Cut/Copy still
  // require a row selection and defer to a real text selection so we never
  // hijack the browser's own copy; Paste is global whenever a clipboard
  // exists. The pill's Paste button does the same thing.
  const keydownRef = useRef<(e: KeyboardEvent) => void>(() => {});
  keydownRef.current = (e: KeyboardEvent) => {
    const el = e.target as HTMLElement | null;
    const tag = el?.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el?.isContentEditable) return;
    const mod = e.metaKey || e.ctrlKey;
    const hasTextSelection = !!window.getSelection()?.toString();
    if (mod && (e.key === 'x' || e.key === 'X')) {
      if (selectedIds.size === 0 || hasTextSelection) return;
      e.preventDefault();
      handleCut();
    } else if (mod && (e.key === 'c' || e.key === 'C')) {
      if (selectedIds.size === 0 || hasTextSelection) return;
      e.preventDefault();
      handleCopy();
    } else if (mod && (e.key === 'v' || e.key === 'V')) {
      if (!clipboard || pasteTargetIsSource) return;
      e.preventDefault();
      handlePaste();
    } else if (e.key === 'Escape' && clipboard) {
      clearClipboard();
    }
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => keydownRef.current(e);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const handleBulkRemove = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Remove ${selectedIds.size} keyword(s)?`)) return;

    setBulkRemoving(true);
    try {
      await api.deleteTopicKeywordsBulk(Array.from(selectedIds));
      triggerLinkedRefresh();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to unlink keywords');
    } finally {
      setBulkRemoving(false);
    }
  };

  const handleRemoveAllMatching = async () => {
    if (!selectedTopic) return;

    const filterDesc = [];
    if (appliedTextFilter) filterDesc.push(`text "${appliedTextFilter}"`);
    if (winnableOnly) filterDesc.push('★ winnable only');
    if (gapFilterActive(appliedGap)) filterDesc.push(gapFilterLabel(appliedGap));

    const confirmMsg = `Remove ALL ${totalCount} keywords matching: ${filterDesc.join(', ') || 'current filters'}?\n\nThis cannot be undone.`;
    if (!window.confirm(confirmMsg)) return;

    setBulkRemoving(true);
    try {
      const result = await api.deleteTopicKeywordsBulkByFilter({
        topic_id: selectedTopic.id,
        keyword_search: appliedTextFilter || null,
        // Must match what's on screen, or "remove all matching" removes rows
        // the user never saw.
        winnable_only: winnableOnly,
        gap_filter: appliedGap,
        has_relevance: false,
        only_active_topics: false,
      });
      alert(`Removed ${result.deleted_count} keywords`);
      triggerLinkedRefresh();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to unlink keywords');
    } finally {
      setBulkRemoving(false);
    }
  };

  const handleCreateCluster = async () => {
    if (selectedIds.size < 2 || !selectedTopic) return;

    setCreatingCluster(true);
    try {
      // Pick the first selected keyword as the parent
      const selectedArr = Array.from(selectedIds);
      const parentId = selectedArr[0];
      const childIds = selectedArr.slice(1);
      // merge-into-cluster, NOT bulk-set-alias-parent. The latter refuses a
      // keyword that already has children — aliasing is one level deep — so
      // selecting two existing groups and pressing Group simply failed. Merging
      // brings each source's group along, which flattens the two levels in one
      // write; doing it as flatten-then-reparent leaves a window where the
      // grouping is neither shape.
      //
      // This verb was reachable only from the keyword link map, which is gone.
      // Rewiring it here rather than losing it was the point of checking.
      await api.mergeIntoCluster(childIds, parentId);
      setSelectedIds(new Set());
      setAllMatchingSelected(false);
      triggerLinkedRefresh();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to create cluster');
    } finally {
      setCreatingCluster(false);
    }
  };

  const handleDissolveCluster = async (parentKw?: TopicKeyword) => {
    const target = parentKw;

    if (!target) return;
    if (!window.confirm('Dissolve this keyword cluster? The keywords will remain but will no longer be grouped.')) return;

    setDissolvingCluster(true);
    try {
      const childIds = target.aliased_children?.map(c => c.id) || [];
      if (childIds.length > 0) {
        await api.bulkSetAliasParent(childIds, null);
      }
        setSelectedIds(new Set());
      triggerLinkedRefresh();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to dissolve cluster');
    } finally {
      setDissolvingCluster(false);
    }
  };


  const handleToggleExpandCluster = (parentId: number) => {
    setExpandedClusterIds(prev => {
      const next = new Set(prev);
      if (next.has(parentId)) {
        next.delete(parentId);
      } else {
        next.add(parentId);
      }
      return next;
    });
  };

  const handleUnlinkFromCluster = async (parentKw: TopicKeyword, topicKeywordId: number) => {
    try {
      await api.bulkSetAliasParent([topicKeywordId], null);
      triggerLinkedRefresh();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to unlink keyword from cluster');
    }
  };

  const handleAddToCluster = async (parentId: number) => {
    if (selectedIds.size === 0) return;

    // Only add unclustered keywords (no alias_parent, no aliased_children)
    const unclusteredIds = Array.from(selectedIds).filter(id => {
      const kw = keywords.find(k => k.id === id);
      return kw && !kw.alias_parent_id && !(kw.aliased_children && kw.aliased_children.length > 0);
    });
    if (unclusteredIds.length === 0) {
      alert('All selected keywords are already in a cluster.');
      return;
    }

    try {
      await api.bulkSetAliasParent(unclusteredIds, parentId);
      setSelectedIds(new Set());
      setAllMatchingSelected(false);
      triggerLinkedRefresh();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to add keywords to cluster');
    }
  };

  const openCreatePagesModal = async (tkIds?: number[]) => {
    setCreatePageTkIds(tkIds || Array.from(selectedIds));
    setCreatePagesModalOpen(true);
  };

  const handlePagesCreated = (result: MakePagesResult) => {
    const createdKeywordIds = new Set(
      result.pages
        .map((p) => keywords.find((k) => k.id === p.topic_keyword_id)?.keyword_id)
        .filter((id): id is number => id != null),
    );
    setCreatePageTkIds([]);
    setSelectedIds(new Set());
    setAllMatchingSelected(false);
    if (result.created_count > 0) {
      fetchKeywords(1, false);
      setHighlightKeywordIds(createdKeywordIds);
    } else {
      alert('No new pages were created. Selected keywords may already have managed pages.');
    }
  };


  /**
   * Regroup only — decide which terms share a page, and stop there.
   *
   * Grouping and linking were briefly one button, on the grounds that both
   * wrote keyword-level trees so the order between them mattered. That reason
   * is gone: links are page-to-page edges in Link Builder now, and regrouping
   * cannot disturb them — it changes which keywords own a page going forward
   * and never deletes a ManagedPage. Grouping belongs to the subject, so it
   * lives on this pane, which is where you tune it.
   *
   * Shares the workspace's `regrouping` flag rather than a local one, so
   * the running chip shows on every tab and the two buttons cannot both fire.
   */
  const handleRegroupTerms = useCallback(async () => {
    if (!selectedProjectId || !selectedTopic || regrouping) return;
    if (
      !window.confirm(
        'Regroup near-duplicate terms onto shared pages, using AI.\n\n' +
          'Groups you set by hand are kept, and links are not touched. What ' +
          'changes is which terms own a page - so some rows below will move ' +
          'under a different one.\n\nContinue?',
      )
    )
      return;
    setRegrouping(true);
    try {
      // Fire-and-forget: the workspace hears terms_regrouped, clears the chip
      // and reports a failure; the listener above refreshes this list.
      await api.regroupTerms(selectedProjectId, selectedTopic.id);
    } catch (err) {
      console.error('Failed to enqueue regroup:', err);
      alert(err instanceof Error ? err.message : 'Failed to start regrouping');
      setRegrouping(false);
    }
  }, [selectedProjectId, selectedTopic, regrouping, setRegrouping]);

  const getRelevanceColor = (score: number | null) => {
    if (score === null) return 'bg-gray-200 dark:bg-gray-700';
    if (score >= 0.9) return 'bg-green-500';
    if (score >= 0.7) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  const getRelevanceTextColor = (score: number | null) => {
    if (score === null) return 'text-gray-400 dark:text-gray-500';
    if (score >= 0.9) return 'text-green-600 dark:text-green-400';
    if (score >= 0.7) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-red-600 dark:text-red-400';
  };

  const formatNumber = (num: number | null) => {
    if (num === null) return '-';
    return num.toLocaleString();
  };

  const SortIcon = ({ column }: { column: SortColumn }) => {
    // In semantic mode, relevance column shows the similarity sort
    const isActive = appliedSemanticQuery
      ? column === 'relevance'
      : sortBy === column;
    if (!isActive) {
      return <span className="ml-1 text-gray-300 dark:text-gray-600">⇅</span>;
    }
    return <span className="ml-1">{sortOrder === 'asc' ? '↑' : '↓'}</span>;
  };

  if (!selectedTopic) {
    return (
      <div className="h-full flex items-center justify-center text-gray-500 dark:text-gray-400">
        Select a topic to view linked keywords
      </div>
    );
  }

  return (
    <div className="relative h-full w-full flex flex-col min-h-0">
      {/* Floating clipboard bar — visible whenever keywords are cut/copied,
          so the clipboard isn't invisible after navigating to another topic. */}
      {clipboard && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-30 flex items-center gap-3 px-4 py-2 rounded-full shadow-lg bg-gray-900 text-white text-sm">
          <span>
            {clipboard.ids.length} keyword{clipboard.ids.length === 1 ? '' : 's'}{' '}
            {clipboard.mode === 'cut' ? 'cut' : 'copied'} from{' '}
            <span className="font-medium">{clipboardSourceName}</span>
          </span>
          <button
            onClick={handlePaste}
            disabled={pasting || pasteTargetIsSource}
            title={
              pasteTargetIsSource
                ? 'Open a different topic to paste'
                : `Paste into ${selectedTopic?.name ?? 'this topic'}`
            }
            className="px-3 py-1 rounded-full bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 font-medium hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {pasting
              ? 'Pasting…'
              : pasteTargetIsSource
                ? 'Paste here'
                : `Paste into ${selectedTopic?.name ?? 'this topic'}`}
            {!pasting && !pasteTargetIsSource && (
              <span className="ml-1.5 text-gray-400 dark:text-gray-500 text-xs">⌘V</span>
            )}
          </button>
          <button
            onClick={clearClipboard}
            className="icon-btn text-gray-300 dark:text-gray-600 hover:text-white"
            title="Clear clipboard (Esc)"
          >
            ✕
          </button>
        </div>
      )}
      {/* Header */}
      <div className="px-4 sm:px-6 lg:px-8 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100 uppercase tracking-wider">
            Keywords
            <span className="ml-2 text-gray-500 dark:text-gray-400 font-normal">
              ({totalCount.toLocaleString()})
            </span>
          </h2>
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                scrollContainerRef.current?.scrollTo(0, 0);
                setWinnableOnly(v => !v);
              }}
              aria-pressed={winnableOnly}
              className={`px-2 py-1 text-xs rounded flex items-center gap-1 ${
                winnableOnly
                  ? 'bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300 hover:bg-amber-200 dark:hover:bg-amber-900/50'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
              }`}
              title={WINNABLE_HELP}
            >
              <span aria-hidden="true">★</span>
              Winnable only
            </button>
            <button
              onClick={() => setFiltersExpanded(!filtersExpanded)}
              className={`px-2 py-1 text-xs rounded flex items-center gap-1 ${
                hasActiveFilters
                  ? 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 hover:bg-purple-200 dark:hover:bg-purple-900/50'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
              }`}
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
              {hasActiveFilters ? 'Filters Active' : 'Filter'}
              <svg
                className={`w-3 h-3 transition-transform ${filtersExpanded ? 'rotate-180' : ''}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            <button
              onClick={handleRegroupTerms}
              disabled={regrouping}
              className="px-2 py-1 text-xs rounded bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-200 dark:hover:bg-indigo-900/50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
              title="Use AI to group near-duplicate terms onto shared pages. Groups you set by hand are kept."
            >
              <svg className={`w-3.5 h-3.5 ${regrouping ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8" />
                <path d="M21 3v5h-5" />
              </svg>
              {regrouping ? 'Regrouping…' : 'Regroup terms'}
            </button>
            {linkedRoom && selectedTopic && (
              <button
                onClick={() => setSeedOpen(true)}
                className="px-2 py-1 text-xs rounded bg-teal-100 dark:bg-teal-900/40 text-teal-700 dark:text-teal-300 hover:bg-teal-200 dark:hover:bg-teal-900/50 flex items-center gap-1"
                title={`Take search terms from “${linkedRoom.name}”`}
              >
                From the room
              </button>
            )}
            {onDiscoverClick && (
              <button
                onClick={onDiscoverClick}
                className="px-3 py-1.5 text-xs font-medium rounded bg-purple-600 text-white hover:bg-purple-700 flex items-center gap-1.5"
              >
                {/* Sparkle — same glyph Quick Suggest uses on the category
                    pages, so "this button asks the LLM" reads the same
                    everywhere. Replaces a bare + that said "add a row". */}
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456z" />
                </svg>
                Discover Keywords
              </button>
            )}
          </div>
        </div>

        {/* Collapsible Filter Panel */}
        {filtersExpanded && (
          <div className="mt-3 p-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md space-y-3">
            {/* Text Search */}
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
                Text search
              </label>
              <input
                type="text"
                value={textFilter}
                onChange={(e) => setTextFilter(e.target.value)}
                placeholder='e.g., "horse" -"mountain biking" -mtb -bike -biking'
                className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
                onKeyDown={(e) => e.key === 'Enter' && handleApplyFilters()}
              />
            </div>

            {/* Semantic Query */}
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
                Semantic sort query
                <span className="ml-1 font-normal text-gray-400 dark:text-gray-500">(sort by similarity)</span>
              </label>
              <input
                type="text"
                value={semanticQuery}
                onChange={(e) => setSemanticQuery(e.target.value)}
                placeholder="e.g., horse riding"
                className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
                onKeyDown={(e) => e.key === 'Enter' && handleApplyFilters()}
              />
              {/* Semantic Threshold Slider */}
              <div className="mt-2 flex items-center gap-3">
                <label className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                  Min similarity to '{semanticQuery || "your query"}': {Math.round(minSemanticSimilarity * 100)}%
                </label>
                <input
                  type="range"
                  min="0"
                  max="0.95"
                  step="0.05"
                  value={minSemanticSimilarity}
                  onChange={(e) => setMinSemanticSimilarity(parseFloat(e.target.value))}
                  className="flex-1 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-purple-600"
                />
              </div>
            </div>

            <SerpGapFilterFields value={gapDraft} onChange={setGapDraft} onEnter={handleApplyFilters} />

            {/* Filter Actions */}
            <div className="flex items-center justify-between pt-2 border-t border-gray-100 dark:border-gray-700">
              <button
                onClick={handleClearFilters}
                className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
              >
                Clear all
              </button>
              <button
                onClick={handleApplyFilters}
                className="px-3 py-1.5 bg-purple-600 text-white text-xs font-medium rounded hover:bg-purple-700"
              >
                Apply Filters
              </button>
            </div>
          </div>
        )}

        {/* Active Filter Tags */}
        {hasActiveFilters && !filtersExpanded && (
          <div className="mt-2 flex flex-wrap items-center gap-2">
            {appliedTextFilter && (
              <span className="px-2 py-0.5 bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 text-xs rounded-full">
                text: {appliedTextFilter}
              </span>
            )}
            {winnableOnly && (
              <span className="px-2 py-0.5 bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300 text-xs rounded-full">
                ★ winnable only
              </span>
            )}
            {gapFilterActive(appliedGap) && (
              <span className="px-2 py-0.5 bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300 text-xs rounded-full">
                {gapFilterLabel(appliedGap)}
              </span>
            )}
            {appliedSemanticQuery && (
              <span className="px-2 py-0.5 bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 text-xs rounded-full">
                similar to: "{appliedSemanticQuery}" ≥{Math.round(appliedMinSemanticSimilarity * 100)}%
              </span>
            )}
            <button
              onClick={handleClearFilters}
              className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
            >
              Clear
            </button>
          </div>
        )}

        {/* Bulk Actions */}
        {selectedIds.size > 0 && (
          <div className="mt-2 flex items-center gap-3 text-sm flex-wrap">
            <span className="text-gray-600 dark:text-gray-300">
              {allMatchingSelected
                ? `All ${totalCount.toLocaleString()}`
                : `${selectedIds.size}`}{' '}
              selected
            </span>
            {/* Show "Select all X" link when all on page are selected but not all matching */}
            {!allMatchingSelected &&
              keywords.length > 0 &&
              selectedIds.size === keywords.length &&
              totalCount > keywords.length && (
                <button
                  onClick={handleSelectAllMatching}
                  className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:underline"
                >
                  Select all {totalCount.toLocaleString()}
                </button>
              )}
            {/* Only show "Create Cluster" when 2+ unclustered keywords are selected */}
            {selectedIds.size >= 2 && (
              <button
                onClick={handleCreateCluster}
                disabled={creatingCluster}
                className="px-2 py-1 bg-indigo-600 text-white text-xs rounded hover:bg-indigo-700 disabled:opacity-50"
              >
                {creatingCluster ? 'Creating...' : `Cluster ${selectedIds.size} Similar Keywords`}
              </button>
            )}
            {/* Add to existing cluster when unclustered keywords are selected */}
            {allClusterParents.length > 0 && selectedIds.size >= 1 && (
              <select
                className="px-2 py-1 text-xs rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300"
                value=""
                onChange={(e) => {
                  if (e.target.value) handleAddToCluster(Number(e.target.value));
                }}
              >
                <option value="">Add to similarity cluster...</option>
                {allClusterParents.map(p => (
                  <option key={p.id} value={p.id}>
                    {p.keyword_value} ({(p.aliased_children?.length || 0) + 1})
                  </option>
                ))}
              </select>
            )}
            <button
              onClick={() => openCreatePagesModal()}
              className="px-2 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700"
            >
              Make {runOutcome(RUN_FORMATS[0], getSelectedCount())}
            </button>
            <button
              onClick={handleFetchSerps}
              disabled={!networkEnabled || fetchingSerps || serpFetchIds.length === 0}
              title={
                !networkEnabled
                  ? 'Network access is disabled. Enable it in Network Settings to fetch SERPs.'
                  : serpFetchIds.length === 0
                    ? `Every selected keyword is being searched or was searched in the last ${BULK_FETCH_FRESH_DAYS} days.`
                    : `Search Google for these keywords. Skips any being searched or searched in the last ${BULK_FETCH_FRESH_DAYS} days; at most ${BULK_FETCH_MAX} per press.`
              }
              className="px-2 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 disabled:opacity-50"
            >
              {fetchingSerps
                ? 'Starting…'
                : `Fetch ${serpFetchIds.length} SERP${serpFetchIds.length === 1 ? '' : 's'}`}
            </button>
            <button
              onClick={handleCut}
              title="Cut - move these keywords to another topic (⌘/Ctrl+X)"
              className="px-2 py-1 bg-slate-600 text-white text-xs rounded hover:bg-slate-700"
            >
              Cut
            </button>
            <button
              onClick={handleCopy}
              title="Copy - add these keywords to another topic (⌘/Ctrl+C)"
              className="px-2 py-1 bg-slate-500 text-white text-xs rounded hover:bg-slate-600"
            >
              Copy
            </button>
            <button
              onClick={() => handleBulkPin(true)}
              className="px-2 py-1 bg-amber-600 text-white text-xs rounded hover:bg-amber-700"
            >
              Pin
            </button>
            <button
              onClick={() => handleBulkPin(false)}
              className="px-2 py-1 bg-gray-400 text-white text-xs rounded hover:bg-gray-500"
            >
              Unpin
            </button>
            <button
              onClick={allMatchingSelected ? handleRemoveAllMatching : handleBulkRemove}
              disabled={bulkRemoving}
              className="px-2 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700 disabled:opacity-50"
            >
              {bulkRemoving ? 'Removing...' : `Remove ${getSelectedCount().toLocaleString()} Keywords`}
            </button>
            <button
              onClick={() => { setSelectedIds(new Set()); setAllMatchingSelected(false); }}
              className="text-xs text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-200"
            >
              Clear
            </button>
          </div>
        )}
      </div>

      {/* Content */}
      <div ref={scrollContainerRef} className="flex-1 overflow-auto min-h-0">
        {loading && keywords.length === 0 ? (
          <div className="p-8 text-center text-gray-500 dark:text-gray-400">Loading...</div>
        ) : keywords.length === 0 ? (
          <div className="p-8 text-center text-gray-500 dark:text-gray-400">
            {hasActiveFilters
              ? 'No keywords match the current filters'
              : 'No keywords linked to this topic'}
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <table className="hidden sm:table min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-800 sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left">
                    <input
                      type="checkbox"
                      checked={keywords.length > 0 && selectedIds.size === keywords.length}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      className="h-4 w-4 text-blue-600 dark:text-blue-400 rounded border-gray-300 dark:border-gray-600"
                    />
                  </th>
                  <th
                    onClick={() => handleSort('cluster_size')}
                    className="px-3 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 w-16"
                    title="Cluster size"
                  >
                    <svg className="w-4 h-4 inline-block" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                    </svg>
                    <SortIcon column="cluster_size" />
                  </th>
                  <th
                    onClick={() => handleSort('keyword')}
                    className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    Keyword
                    <SortIcon column="keyword" />
                  </th>
                  <th
                    onClick={() => handleSort('has_page')}
                    className="px-3 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 w-24"
                  >
                    Page
                    <SortIcon column="has_page" />
                  </th>
                  <th
                    onClick={() => handleSort('latest_serp_date')}
                    className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    SERPs
                    <SortIcon column="latest_serp_date" />
                  </th>
                  <th
                    onClick={() => handleSort('relevance')}
                    className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 w-32"
                  >
                    {appliedSemanticQuery ? `Similarity to ` + semanticQuery : 'Relevance to topic'}
                    <SortIcon column="relevance" />
                  </th>
                  <th
                    onClick={() => handleSort('avg_monthly_searches')}
                    className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 w-28"
                  >
                    LT Avg
                    <SortIcon column="avg_monthly_searches" />
                  </th>
                  <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase w-[110px]">
                    Trend
                  </th>
                  <th className="px-3 py-2 w-16"></th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {keywordRows.map((row) => {
                  if (row.type === 'cluster_header') {
                    const { parent, searchSum } = row;
                    const isExpanded = expandedClusterIds.has(parent.id);

                    const isParentHighlighted = highlightKeywordIds.has(parent.keyword_id);
                    return (
                      <React.Fragment key={`cluster-${parent.id}`}>
                        {/* Cluster header row — cells aligned to table columns */}
                        <tr
                          data-keyword-id={parent.keyword_id}
                          className={`group transition-colors duration-1000 ${isCutRow(parent.id) ? 'opacity-40' : ''} ${isParentHighlighted ? 'bg-yellow-100 dark:bg-yellow-900/40 ring-1 ring-inset ring-yellow-300' : 'bg-violet-50/60 dark:bg-violet-900/30'}`}
                        >
                          {/* Checkbox column */}
                          <td className="px-3 py-2">
                            <button
                              onClick={(e) => { e.stopPropagation(); handleToggleExpandCluster(parent.id); }}
                              className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
                            >
                              <svg
                                className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                                fill="none" viewBox="0 0 24 24" stroke="currentColor"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                              </svg>
                            </button>
                          </td>
                          {/* Cluster size column */}
                          <td className="px-3 py-2 text-center">
                            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-semibold bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300">
                              {parent.aliased_children.length + 1}
                            </span>
                          </td>
                          {/* Keyword column */}
                          <td className="px-3 py-2">
                            <div className="flex items-center gap-2 min-w-0">
                              <span className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                                {parent.keyword_value}
                                <WinnableStar kw={parent} />
                                <SerpGapBadge gaps={parent.serp_gaps} className="ml-1" />
                                {parent.user_pinned && <span className="ml-1 text-amber-500 text-xs" title="Pinned">&#x1F4CC;</span>}
                              </span>
                              {!isExpanded && (
                                <span className="text-xs text-gray-400 dark:text-gray-500 truncate flex-shrink-0">
                                  +{parent.aliased_children.length} more
                                </span>
                              )}
                            </div>
                          </td>
                          {/* Page column */}
                          <td className="px-3 py-2"></td>
                          {/* SERPs column - bulk summary for all members */}
                          <td className="px-3 py-2">
                            {(() => {
                              const allMembers = [parent, ...parent.aliased_children];
                              const total = allMembers.length;
                              const withSerps = allMembers.filter(m => m.latest_serp_date).length;
                              const allPending = allMembers.every(m => m.has_pending_serp_request);
                              const somePending = allMembers.some(m => m.has_pending_serp_request);
                              return (
                                <div className="flex items-center gap-1.5">
                                  {allPending ? (
                                    <span className="inline-flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-400">
                                      <RefreshGlyph spinning />
                                      <span>Pending</span>
                                    </span>
                                  ) : (
                                    <>
                                      <span className="text-xs text-gray-600 dark:text-gray-300">
                                        {withSerps === total ? `${total} SERPs` : `${withSerps} / ${total} SERPs`}
                                      </span>
                                      {somePending ? (
                                        <span className="text-amber-500" title="Some requests pending">
                                          <RefreshGlyph spinning />
                                        </span>
                                      ) : (
                                        <button
                                          onClick={(e) => { e.stopPropagation(); handleBulkRequestSerp(parent); }}
                                          disabled={requestingBulkSerpFor === parent.id || !networkEnabled}
                                          className="text-gray-400 dark:text-gray-500 hover:text-blue-600 dark:hover:text-blue-400 disabled:opacity-50"
                                          title={!networkEnabled ? 'Network access is disabled. Enable it in Network Settings to fetch SERPs.' : withSerps === total ? 'Refresh all SERPs' : 'Request missing SERPs'}
                                        >
                                          <RefreshGlyph />
                                        </button>
                                      )}
                                    </>
                                  )}
                                </div>
                              );
                            })()}
                          </td>
                          {/* Relevance column */}
                          <td className="px-3 py-2"></td>
                          {/* Searches column */}
                          <td className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400 text-right">
                            {searchSum > 0 ? searchSum.toLocaleString() : '-'}
                          </td>
                          {/* Trend column */}
                          <td className="px-2 py-2"></td>
                          {/* Actions column */}
                          <td className="px-3 py-2 text-right">
                            <div className="flex items-center justify-end gap-2">
                              <button
                                onClick={(e) => { e.stopPropagation(); openCreatePagesModal([parent.id]); }}
                                className="px-2 py-0.5 text-xs font-medium text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded hover:bg-green-100 dark:hover:bg-green-900/40"
                                title="Create managed page"
                              >
                                +Page
                              </button>
                              <button
                                onClick={(e) => { e.stopPropagation(); handleDissolveCluster(parent); }}
                                className="text-xs text-amber-600 dark:text-amber-400 hover:text-amber-800 dark:hover:text-amber-300"
                                title="Dissolve this keyword cluster"
                              >
                                Dissolve
                              </button>
                            </div>
                          </td>
                        </tr>
                        {/* Expanded: primary keyword first, then aliased children */}
                        {isExpanded && (
                          <tr
                            key={`primary-${parent.id}`}
                            className={`border-b border-violet-100/50 dark:border-violet-800 bg-violet-50/60 dark:bg-violet-900/30 bg-opacity-30`}
                          >
                            <td className="px-3 py-1.5"></td>
                            <td className="px-3 py-1.5"></td>
                            <td className="px-3 py-1.5 pl-12">
                              <span className="text-sm text-gray-700 dark:text-gray-300">{parent.keyword_value}</span>
                            </td>
                            <td className="px-3 py-1.5">
                              {parent.managed_pages && parent.managed_pages.length > 0 && (
                                <div className="flex flex-wrap gap-1 justify-center">
                                  {parent.managed_pages.map((pg) => (
                                    <SitePlanChip
                                      key={pg.id}
                                      siteName={pg.managed_site_name}
                                      planName={pg.deployment_plan_name}
                                      status={pg.status}
                                      onClick={() =>
                                        navigate(
                                          withReturnTo(
                                            `/page-builder/${pg.id}`,
                                            `${here.pathname}${here.search}`,
                                            'the subject',
                                          ),
                                        )
                                      }
                                    />
                                  ))}
                                </div>
                              )}
                            </td>
                            <td className="px-3 py-1.5">
                              <div className="flex items-center gap-2">
                                {parent.latest_serp_date ? (
                                  <button
                                    onClick={(e) => { e.stopPropagation(); setSerpModalKeyword({ id: parent.keyword_id, value: parent.keyword_value }); }}
                                    className="inline-flex items-center gap-1.5 text-xs text-gray-700 dark:text-gray-300 hover:text-indigo-600 dark:hover:text-indigo-400"
                                    title={`View ${parent.serp_count} SERP result(s)`}
                                  >
                                    <EngineIcon engine={parent.latest_serp_engine} size="sm" />
                                    <span className="font-medium">{formatSerpDate(parent.latest_serp_date)}</span>
                                  </button>
                                ) : parent.has_pending_serp_request ? (
                                  <span className="inline-flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-400">
                                    <RefreshGlyph spinning /><span>Pending</span>
                                  </span>
                                ) : (
                                  <button
                                    onClick={(e) => { e.stopPropagation(); handleRequestSerp(parent.keyword_id); }}
                                    disabled={requestingSerpFor === parent.keyword_id || !networkEnabled}
                                    title={networkEnabled ? undefined : 'Network access is disabled. Enable it in Network Settings to fetch SERPs.'}
                                    className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 disabled:opacity-50"
                                  >
                                    {requestingSerpFor === parent.keyword_id ? '...' : 'Fetch SERP'}
                                  </button>
                                )}
                                <SerpFailedNote row={parent} />
                                <ExternalSearchLink engine={parent.latest_serp_engine} keyword={parent.keyword_value} />
                              </div>
                            </td>
                            <td className="px-3 py-1.5">
                              {parent.topic_relevance_score !== null && (
                                <span className={`text-xs font-medium ${getRelevanceTextColor(parent.topic_relevance_score)}`}>
                                  {Math.round(parent.topic_relevance_score * 100)}%
                                </span>
                              )}
                            </td>
                            <td className="px-3 py-1.5 text-sm text-gray-500 dark:text-gray-400 text-right">
                              {formatNumber(parent.avg_monthly_searches)}
                            </td>
                            <td className="px-2 py-1.5">
                              {(() => { const md = monthlyDataMap.get(parent.keyword_id); return md && md.length > 1 ? (
                                <div className="flex justify-center">
                                  <LineChart width={80} height={24} data={md}>
                                    <Tooltip offset={0} position={{ y: -30 }} content={({ active, payload }) => {
                                      if (!active || !payload?.[0]) return null;
                                      const d = payload[0].payload;
                                      return <div className="bg-gray-900 text-white text-xs px-2 py-1 rounded shadow whitespace-nowrap">{d.month} {d.year}: {Number(d.searches).toLocaleString()}</div>;
                                    }} />
                                    <Line type="monotone" dataKey="searches" stroke={parent.growth_pct != null && parent.growth_pct > 0 ? '#16a34a' : '#dc2626'} dot={false} strokeWidth={1} isAnimationActive={false} />
                                  </LineChart>
                                </div>
                              ) : loadingMonthly ? (
                                <div className="w-[80px] h-[24px] bg-gray-100 dark:bg-gray-700 rounded animate-pulse" />
                              ) : null; })()}
                            </td>
                            <td className="px-3 py-1.5 text-right">
                              <span className="text-xs text-gray-400 dark:text-gray-500">primary</span>
                            </td>
                          </tr>
                        )}
                        {isExpanded && parent.aliased_children.map((child: AliasedChild) => (
                          <tr
                            key={`member-${child.id}`}
                            className={`group/member border-b border-violet-100/50 dark:border-violet-800 last:border-b-0 bg-violet-50/60 dark:bg-violet-900/30 bg-opacity-30`}
                          >
                            <td className="px-3 py-1.5"></td>
                            <td className="px-3 py-1.5"></td>
                            <td className="px-3 py-1.5 pl-12">
                              <span className="text-sm text-gray-700 dark:text-gray-300">{child.keyword_value}</span>
                            </td>
                            <td className="px-3 py-1.5"></td>
                            <td className="px-3 py-1.5">
                              <div className="flex items-center gap-2">
                                {child.latest_serp_date ? (
                                  <button
                                    onClick={() => setSerpModalKeyword({ id: child.keyword_id, value: child.keyword_value })}
                                    className="inline-flex items-center gap-1.5 text-xs text-gray-700 dark:text-gray-300 hover:text-indigo-600 dark:hover:text-indigo-400"
                                    title={`View ${child.serp_count} SERP result(s)`}
                                  >
                                    <EngineIcon engine={child.latest_serp_engine} size="sm" />
                                    <span className="font-medium">{formatSerpDate(child.latest_serp_date)}</span>
                                  </button>
                                ) : child.has_pending_serp_request ? (
                                  <span className="inline-flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-400">
                                    <RefreshGlyph spinning /><span>Pending</span>
                                    <button
                                      onClick={() => handleCancelSerp(child.keyword_id)}
                                      className="icon-btn text-amber-500 hover:text-red-600 dark:hover:text-red-400"
                                      title="Cancel this SERP crawl"
                                      aria-label="Cancel SERP crawl"
                                    >
                                      ✕
                                    </button>
                                  </span>
                                ) : (
                                  <button
                                    onClick={() => handleRequestSerp(child.keyword_id)}
                                    disabled={requestingSerpFor === child.keyword_id || !networkEnabled}
                                    title={networkEnabled ? undefined : 'Network access is disabled. Enable it in Network Settings to fetch SERPs.'}
                                    className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 disabled:opacity-50"
                                  >
                                    {requestingSerpFor === child.keyword_id ? '...' : 'Fetch SERP'}
                                  </button>
                                )}
                                <SerpFailedNote row={child} />
                                <ExternalSearchLink engine={child.latest_serp_engine} keyword={child.keyword_value} />
                              </div>
                            </td>
                            <td className="px-3 py-1.5">
                              {child.topic_relevance_score !== null && (
                                <span className={`text-xs font-medium ${getRelevanceTextColor(child.topic_relevance_score)}`}>
                                  {Math.round(child.topic_relevance_score * 100)}%
                                </span>
                              )}
                            </td>
                            <td className="px-3 py-1.5 text-sm text-gray-500 dark:text-gray-400 text-right">
                              {formatNumber(child.avg_monthly_searches)}
                            </td>
                            <td className="px-2 py-1.5">
                              {(() => { const md = monthlyDataMap.get(child.keyword_id); return md && md.length > 1 ? (
                                <div className="flex justify-center">
                                  <LineChart width={80} height={24} data={md}>
                                    <Tooltip offset={0} position={{ y: -30 }} content={({ active, payload }: any) => {
                                      if (!active || !payload?.[0]) return null;
                                      const d = payload[0].payload;
                                      return <div className="bg-gray-900 text-white text-xs px-2 py-1 rounded shadow whitespace-nowrap">{d.month} {d.year}: {Number(d.searches).toLocaleString()}</div>;
                                    }} />
                                    <Line type="monotone" dataKey="searches" stroke={child.growth_pct != null && child.growth_pct > 0 ? '#16a34a' : '#dc2626'} dot={false} strokeWidth={1} isAnimationActive={false} />
                                  </LineChart>
                                </div>
                              ) : loadingMonthly ? (
                                <div className="w-[80px] h-[24px] bg-gray-100 dark:bg-gray-700 rounded animate-pulse" />
                              ) : null; })()}
                            </td>
                            <td className="px-3 py-1.5 text-right">
                              <button
                                onClick={() => handleUnlinkFromCluster(parent, child.id)}
                                className="text-xs text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300"
                                title="Unlink from cluster of aliases"
                              >
                                Unlink
                              </button>
                            </td>
                          </tr>
                        ))}
                      </React.Fragment>
                    );
                  }

                  // Unclustered keyword row
                  const kw = row.keyword;
                  const isHighlighted = highlightKeywordIds.has(kw.keyword_id);
                  return (
                    <tr
                      key={kw.id}
                      data-keyword-id={kw.keyword_id}
                      className={`${isCutRow(kw.id) ? 'opacity-40' : ''} ${isHighlighted ? 'bg-yellow-100 dark:bg-yellow-900/40 ring-1 ring-inset ring-yellow-300' : selectedIds.has(kw.id) ? 'bg-blue-50 dark:bg-blue-900/30' : 'hover:bg-gray-50 dark:hover:bg-gray-800'} transition-colors duration-1000`}
                    >
                      <td className="px-3 py-2">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(kw.id)}
                          onChange={(e) => handleSelectOne(kw.id, e.target.checked)}
                          className="h-4 w-4 text-blue-600 dark:text-blue-400 rounded border-gray-300 dark:border-gray-600"
                        />
                      </td>
                      <td className="px-3 py-2"></td>
                      <td className="px-3 py-2">
                        <span className="text-sm text-gray-900 dark:text-gray-100">{kw.keyword_value}</span>
                        <WinnableStar kw={kw} />
                        <SerpGapBadge gaps={kw.serp_gaps} className="ml-1" />
                        {kw.user_pinned && <span className="ml-1 text-amber-500 text-xs" title="Pinned - protected from reorganization">&#x1F4CC;</span>}
                      </td>
                      <td className="px-3 py-2">
                        {kw.managed_pages && kw.managed_pages.length > 0 && (
                          <div className="flex flex-wrap gap-1 justify-center">
                            {kw.managed_pages.map((page) => (
                              <SitePlanChip
                                key={page.id}
                                siteName={page.managed_site_name}
                                planName={page.deployment_plan_name}
                                status={page.status}
                                onClick={() =>
                                        navigate(
                                          withReturnTo(
                                            `/page-builder/${page.id}`,
                                            `${here.pathname}${here.search}`,
                                            'the subject',
                                          ),
                                        )
                                      }
                              />
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2">
                          {kw.latest_serp_date ? (
                            <button
                              onClick={() => setSerpModalKeyword({ id: kw.keyword_id, value: kw.keyword_value })}
                              className="inline-flex items-center gap-1.5 text-xs text-gray-700 dark:text-gray-300 hover:text-indigo-600 dark:hover:text-indigo-400"
                              title={`View ${kw.serp_count} SERP result(s)`}
                            >
                              <EngineIcon engine={kw.latest_serp_engine} size="sm" />
                              <span className="font-medium">{formatSerpDate(kw.latest_serp_date)}</span>
                            </button>
                          ) : kw.has_pending_serp_request ? (
                            <span className="inline-flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-400">
                              <RefreshGlyph spinning />
                              <span>Pending...</span>
                              <button
                                onClick={() => handleCancelSerp(kw.keyword_id)}
                                className="icon-btn text-amber-500 hover:text-red-600 dark:hover:text-red-400"
                                title="Cancel this SERP crawl"
                                aria-label="Cancel SERP crawl"
                              >
                                ✕
                              </button>
                            </span>
                          ) : (
                            <button
                              onClick={() => handleRequestSerp(kw.keyword_id)}
                              disabled={requestingSerpFor === kw.keyword_id || !networkEnabled}
                              className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 disabled:opacity-50"
                              title={networkEnabled ? 'Request SERP' : 'Network access is disabled. Enable it in Network Settings to fetch SERPs.'}
                            >
                              {requestingSerpFor === kw.keyword_id ? '...' : 'Fetch SERP'}
                            </button>
                          )}
                          <SerpFailedNote row={kw} />
                          <ExternalSearchLink engine={kw.latest_serp_engine} keyword={kw.keyword_value} />
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        {kw.topic_relevance_score !== null ? (
                          <div className="flex items-center gap-2">
                            <div className="w-16 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                              <div
                                className={`h-full ${getRelevanceColor(kw.topic_relevance_score)}`}
                                style={{
                                  width: `${kw.topic_relevance_score * 100}%`,
                                }}
                              />
                            </div>
                            <span
                              className={`text-xs font-medium ${getRelevanceTextColor(
                                kw.topic_relevance_score
                              )}`}
                            >{
                              `${Math.round(kw.topic_relevance_score * 100)}%`
                            }
                            </span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <div className="w-16 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                              <div className="h-full w-full bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 animate-pulse" />
                            </div>
                            <span className="text-xs text-gray-400 dark:text-gray-500 italic">
                              Computing...
                            </span>
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400 text-right">
                        {formatNumber(kw.avg_monthly_searches)}
                      </td>
                      <td className="px-2 py-2">
                        {(() => { const md = monthlyDataMap.get(kw.keyword_id); return md && md.length > 1 ? (
                          <div className="flex justify-center">
                            <LineChart width={100} height={30} data={md}>
                              <Tooltip
                                offset={0}
                                position={{ y: -30 }}
                                content={({ active, payload }) => {
                                  if (!active || !payload?.[0]) return null;
                                  const d = payload[0].payload;
                                  return (
                                    <div className="bg-gray-900 text-white text-xs px-2 py-1 rounded shadow whitespace-nowrap">
                                      {d.month} {d.year}: {Number(d.searches).toLocaleString()}
                                    </div>
                                  );
                                }}
                              />
                              <Line
                                type="monotone"
                                dataKey="searches"
                                stroke={kw.growth_pct != null && kw.growth_pct > 0 ? '#16a34a' : '#dc2626'}
                                dot={false}
                                strokeWidth={1.5}
                                isAnimationActive={false}
                              />
                            </LineChart>
                          </div>
                        ) : loadingMonthly ? (
                          <div className="w-[100px] h-[30px] bg-gray-100 dark:bg-gray-700 rounded animate-pulse" />
                        ) : (
                          <span className="text-gray-300 dark:text-gray-600 text-xs text-center block">-</span>
                        ); })()}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => openCreatePagesModal([kw.id])}
                          className="px-2 py-0.5 text-xs font-medium text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded hover:bg-green-100 dark:hover:bg-green-900/40"
                          title="Create managed page"
                        >
                          +Page
                        </button>
                        <button
                          onClick={() => handleRemove(kw)}
                          className="text-xs text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300"
                          title="Remove keyword from topic"
                        >
                          Remove
                        </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Mobile Card List */}
            <div className="sm:hidden divide-y divide-gray-200 dark:divide-gray-700">
              {/* Mobile header with select all */}
              <div className="px-3 py-2 bg-gray-50 dark:bg-gray-800 flex items-center justify-between sticky top-0">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={keywords.length > 0 && selectedIds.size === keywords.length}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    className="h-5 w-5 text-blue-600 dark:text-blue-400 rounded border-gray-300 dark:border-gray-600"
                  />
                  <span className="text-xs text-gray-500 dark:text-gray-400">Select all</span>
                </label>
                <div className="flex gap-1">
                  <button
                    onClick={() => handleSort('relevance')}
                    className={`px-2 py-1 text-xs rounded ${
                      sortBy === 'relevance' || appliedSemanticQuery
                        ? 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                    }`}
                  >
                    {appliedSemanticQuery ? 'Sim' : 'Rel'} {(sortBy === 'relevance' || appliedSemanticQuery) && (sortOrder === 'asc' ? '↑' : '↓')}
                  </button>
                  <button
                    onClick={() => handleSort('avg_monthly_searches')}
                    className={`px-2 py-1 text-xs rounded ${
                      sortBy === 'avg_monthly_searches' && !appliedSemanticQuery
                        ? 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                    }`}
                  >
                    LT Avg {sortBy === 'avg_monthly_searches' && !appliedSemanticQuery && (sortOrder === 'asc' ? '↑' : '↓')}
                  </button>
                  <button
                    onClick={() => handleSort('keyword')}
                    className={`px-2 py-1 text-xs rounded ${
                      sortBy === 'keyword' && !appliedSemanticQuery
                        ? 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                    }`}
                  >
                    A-Z {sortBy === 'keyword' && !appliedSemanticQuery && (sortOrder === 'asc' ? '↑' : '↓')}
                  </button>
                  <button
                    onClick={() => handleSort('cluster_size')}
                    className={`px-2 py-1 text-xs rounded ${
                      sortBy === 'cluster_size' && !appliedSemanticQuery
                        ? 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                    }`}
                  >
                    Cluster {sortBy === 'cluster_size' && !appliedSemanticQuery && (sortOrder === 'asc' ? '↑' : '↓')}
                  </button>
                  <button
                    onClick={() => handleSort('has_page')}
                    className={`px-2 py-1 text-xs rounded ${
                      sortBy === 'has_page' && !appliedSemanticQuery
                        ? 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                    }`}
                  >
                    Page {sortBy === 'has_page' && !appliedSemanticQuery && (sortOrder === 'asc' ? '↑' : '↓')}
                  </button>
                  <button
                    onClick={() => handleSort('latest_serp_date')}
                    className={`px-2 py-1 text-xs rounded ${
                      sortBy === 'latest_serp_date' && !appliedSemanticQuery
                        ? 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                    }`}
                  >
                    SERPs {sortBy === 'latest_serp_date' && !appliedSemanticQuery && (sortOrder === 'asc' ? '↑' : '↓')}
                  </button>
                </div>
              </div>

              {/* Mobile cards */}
              {keywordRows.map((row) => {
                if (row.type === 'cluster_header') {
                  const { parent, searchSum } = row;
                  const isExpanded = expandedClusterIds.has(parent.id);

                  return (
                    <div key={`cluster-${parent.id}`}>
                      <div
                        className="px-3 py-3 bg-violet-50/60 dark:bg-violet-900/30"
                      >
                        <div className="flex items-center gap-2">
                          <button
                            onClick={(e) => { e.stopPropagation(); handleToggleExpandCluster(parent.id); }}
                            className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 flex-shrink-0"
                          >
                            <svg
                              className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                              fill="none" viewBox="0 0 24 24" stroke="currentColor"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </button>
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300">
                            {parent.aliased_children.length + 1}
                          </span>
                          <span className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate flex-1">{parent.keyword_value}</span>
                          {!isExpanded && (
                            <span className="text-xs text-gray-400 dark:text-gray-500">+{parent.aliased_children.length}</span>
                          )}
                          {searchSum > 0 && (
                            <span className="text-xs text-gray-500 dark:text-gray-400">{searchSum.toLocaleString()} /mo</span>
                          )}
                          <button
                            onClick={(e) => { e.stopPropagation(); openCreatePagesModal([parent.id]); }}
                            className="px-2 py-0.5 text-xs font-medium text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded hover:bg-green-100 dark:hover:bg-green-900/40 flex-shrink-0"
                            title="Create managed page"
                          >
                            +Page
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleDissolveCluster(parent); }}
                            className="text-xs text-amber-600 dark:text-amber-400 hover:text-amber-800 dark:hover:text-amber-300 flex-shrink-0"
                            title="Dissolve this keyword cluster"
                          >
                            Dissolve
                          </button>
                        </div>
                        <div className="mt-1.5 pl-10 flex items-center gap-1.5 text-xs">
                          {(() => {
                            const allMembers = [parent, ...parent.aliased_children];
                            const total = allMembers.length;
                            const withSerps = allMembers.filter(m => m.latest_serp_date).length;
                            const allPending = allMembers.every(m => m.has_pending_serp_request);
                            const somePending = allMembers.some(m => m.has_pending_serp_request);
                            return allPending ? (
                              <span className="inline-flex items-center gap-1 text-amber-600 dark:text-amber-400">
                                <RefreshGlyph spinning /><span>Pending</span>
                              </span>
                            ) : (
                              <>
                                <span className="text-gray-600 dark:text-gray-300">
                                  {withSerps === total ? `${total} SERPs` : `${withSerps} / ${total} SERPs`}
                                </span>
                                {somePending ? (
                                  <span className="text-amber-500"><RefreshGlyph spinning /></span>
                                ) : (
                                  <button
                                    onClick={(e) => { e.stopPropagation(); handleBulkRequestSerp(parent); }}
                                    disabled={requestingBulkSerpFor === parent.id || !networkEnabled}
                                    className="text-gray-400 dark:text-gray-500 hover:text-blue-600 dark:hover:text-blue-400 disabled:opacity-50"
                                    title={networkEnabled ? undefined : 'Network access is disabled. Enable it in Network Settings to fetch SERPs.'}
                                  ><RefreshGlyph /></button>
                                )}
                              </>
                            );
                          })()}
                        </div>
                      </div>
                      {isExpanded && (
                        <div key={`primary-${parent.id}`} className="px-3 py-2 pl-12 bg-violet-50/30 dark:bg-violet-900/30 border-t border-violet-100/50 dark:border-violet-800">
                          <div className="flex items-start justify-between">
                            <div>
                              <span className="text-sm text-gray-700 dark:text-gray-300">{parent.keyword_value}</span>
                              <div className="mt-1 flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                                {parent.topic_relevance_score !== null && (
                                  <span className={getRelevanceTextColor(parent.topic_relevance_score)}>
                                    {Math.round(parent.topic_relevance_score * 100)}%
                                  </span>
                                )}
                                {parent.avg_monthly_searches !== null && (
                                  <span>{formatNumber(parent.avg_monthly_searches)} /mo</span>
                                )}
                              </div>
                            </div>
                            <span className="text-xs text-gray-400 dark:text-gray-500 flex-shrink-0 mt-0.5">primary</span>
                          </div>
                          <div className="mt-1.5 flex items-center gap-2 text-xs">
                            {parent.latest_serp_date ? (
                              <button
                                onClick={(e) => { e.stopPropagation(); setSerpModalKeyword({ id: parent.keyword_id, value: parent.keyword_value }); }}
                                className="inline-flex items-center gap-1 text-gray-600 dark:text-gray-300 hover:text-indigo-600 dark:hover:text-indigo-400"
                                title={`${parent.serp_count} SERP run${parent.serp_count === 1 ? '' : 's'}`}
                              >
                                <EngineIcon engine={parent.latest_serp_engine} size="sm" />
                                <span>{formatSerpDate(parent.latest_serp_date)}</span>
                              </button>
                            ) : parent.has_pending_serp_request ? (
                              <span className="inline-flex items-center gap-1 text-amber-600 dark:text-amber-400">
                                <RefreshGlyph spinning /><span>Pending</span>
                              </span>
                            ) : (
                              <button
                                onClick={(e) => { e.stopPropagation(); handleRequestSerp(parent.keyword_id); }}
                                disabled={requestingSerpFor === parent.keyword_id}
                                className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 disabled:opacity-50"
                              >
                                {requestingSerpFor === parent.keyword_id ? '...' : 'Fetch SERP'}
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                      {isExpanded && parent.aliased_children.map((child: AliasedChild) => (
                        <div key={child.id} className="px-3 py-2 pl-12 bg-violet-50/30 dark:bg-violet-900/30 border-t border-violet-100/50 dark:border-violet-800">
                          <div className="flex items-start justify-between">
                            <div>
                              <span className="text-sm text-gray-700 dark:text-gray-300">{child.keyword_value}</span>
                              <div className="mt-1 flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                                {child.topic_relevance_score !== null && (
                                  <span className={getRelevanceTextColor(child.topic_relevance_score)}>
                                    {Math.round(child.topic_relevance_score * 100)}%
                                  </span>
                                )}
                              {child.avg_monthly_searches !== null && (
                                <span>{formatNumber(child.avg_monthly_searches)} /mo</span>
                              )}
                            </div>
                          </div>
                          <button
                            onClick={() => handleUnlinkFromCluster(parent, child.id)}
                            className="text-red-400 hover:text-red-600 dark:hover:text-red-400 p-1 flex-shrink-0"
                            title="Unlink from cluster"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                          </div>
                          <div className="mt-1.5 flex items-center gap-2 text-xs">
                            {child.latest_serp_date ? (
                              <button
                                onClick={() => setSerpModalKeyword({ id: child.keyword_id, value: child.keyword_value })}
                                className="inline-flex items-center gap-1 text-gray-600 dark:text-gray-300 hover:text-indigo-600 dark:hover:text-indigo-400"
                                title={`${child.serp_count ?? 0} SERP run${child.serp_count === 1 ? '' : 's'}`}
                              >
                                <EngineIcon engine={child.latest_serp_engine} size="sm" />
                                <span>{formatSerpDate(child.latest_serp_date)}</span>
                              </button>
                            ) : child.has_pending_serp_request ? (
                              <span className="inline-flex items-center gap-1 text-amber-600 dark:text-amber-400">
                                <RefreshGlyph spinning /><span>Pending</span>
                              </span>
                            ) : (
                              <button
                                onClick={() => handleRequestSerp(child.keyword_id)}
                                disabled={requestingSerpFor === child.keyword_id}
                                className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 disabled:opacity-50"
                              >
                                {requestingSerpFor === child.keyword_id ? '...' : 'Fetch SERP'}
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                }

                const kw = row.keyword;
                const isHighlighted = highlightKeywordIds.has(kw.keyword_id);
                return (
                  <div
                    key={kw.id}
                    data-keyword-id={kw.keyword_id}
                    className={`px-3 py-3 transition-colors duration-1000 ${isCutRow(kw.id) ? 'opacity-40' : ''} ${isHighlighted ? 'bg-yellow-100 dark:bg-yellow-900/40 ring-1 ring-inset ring-yellow-300' : selectedIds.has(kw.id) ? 'bg-blue-50 dark:bg-blue-900/30' : 'bg-white dark:bg-gray-800'}`}
                  >
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(kw.id)}
                        onChange={(e) => handleSelectOne(kw.id, e.target.checked)}
                        className="h-5 w-5 mt-0.5 text-blue-600 dark:text-blue-400 rounded border-gray-300 dark:border-gray-600 flex-shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100 break-words">
                          {kw.keyword_value}
                          <WinnableStar kw={kw} />
                        </span>
                        <SerpGapBadge gaps={kw.serp_gaps} className="ml-1" />
                        <div className="mt-1.5 flex items-center gap-3 text-xs">
                          {kw.topic_relevance_score !== null ? (
                            <div className="flex items-center gap-1.5">
                              <div className="w-12 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                                <div
                                  className={`h-full ${getRelevanceColor(kw.topic_relevance_score)}`}
                                  style={{ width: `${(kw.topic_relevance_score ?? 0) * 100}%` }}
                                />
                              </div>
                              <span className={`font-medium ${getRelevanceTextColor(kw.topic_relevance_score)}`}>
                                {Math.round(kw.topic_relevance_score * 100)}%
                              </span>
                            </div>
                          ) : (
                            <span className="text-gray-400 dark:text-gray-500 italic">Computing...</span>
                          )}
                          {kw.avg_monthly_searches !== null && (
                            <span className="text-gray-500 dark:text-gray-400">{formatNumber(kw.avg_monthly_searches)} /mo</span>
                          )}
                          {kw.latest_serp_date ? (
                            <button
                              onClick={() => setSerpModalKeyword({ id: kw.keyword_id, value: kw.keyword_value })}
                              className="inline-flex items-center gap-1 text-gray-600 dark:text-gray-300 hover:text-indigo-600 dark:hover:text-indigo-400"
                              title={`${kw.serp_count} SERP run${kw.serp_count === 1 ? '' : 's'}`}
                            >
                              <EngineIcon engine={kw.latest_serp_engine} size="sm" />
                              <span>{formatSerpDate(kw.latest_serp_date)}</span>
                            </button>
                          ) : kw.has_pending_serp_request ? (
                            <span className="inline-flex items-center gap-1 text-amber-600 dark:text-amber-400">
                              <RefreshGlyph spinning />
                              <span>Pending</span>
                            </span>
                          ) : (
                            <button
                              onClick={() => handleRequestSerp(kw.keyword_id)}
                              disabled={requestingSerpFor === kw.keyword_id || !networkEnabled}
                              title={networkEnabled ? undefined : 'Network access is disabled. Enable it in Network Settings to fetch SERPs.'}
                              className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 disabled:opacity-50"
                            >
                              {requestingSerpFor === kw.keyword_id ? '...' : 'Fetch SERP'}
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col gap-1 flex-shrink-0">
                        <button
                          onClick={() => openCreatePagesModal([kw.id])}
                          className="px-2 py-1 text-xs font-medium text-green-700 dark:text-green-300 hover:bg-green-50 dark:hover:bg-green-900/30 rounded"
                        >
                          +Page
                        </button>
                        <button
                          onClick={() => handleRemove(kw)}
                          className="px-2 py-1 text-xs text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/30 rounded"
                        >
                          Unlink
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Infinite scroll indicators */}
            <LoadingMore isLoading={loadingMore} />
            <EndOfList show={!hasMore && !loading} itemCount={keywords.length} />
          </>
        )}
      </div>

      {/* SERP Modal */}
      {serpModalKeyword && (
        <SerpModal
          isOpen={true}
          onClose={() => setSerpModalKeyword(null)}
          keywordId={serpModalKeyword.id}
          keywordValue={serpModalKeyword.value}
        />
      )}

      <MakePagesDialog
        open={createPagesModalOpen}
        onClose={() => { setCreatePagesModalOpen(false); setCreatePageTkIds([]); }}
        selectedCount={selectedForRun}
        // Where the run is coming FROM. The dialog turns this into the publish
        // path it prefills, so the page lands where the research says before
        // anyone touches it.
        seedTopicId={selectedTopic?.id ?? null}
        target={
          allMatchingSelected && selectedTopic
            ? {
                kind: 'filter',
                topicId: selectedTopic.id,
                keywordSearch: appliedTextFilter || undefined,
                semanticSearch: appliedSemanticQuery || undefined,
                minSemSimilarity: appliedMinSemanticSimilarity,
              }
            : { kind: 'ids', ids: createPageTkIds }
        }
        onCreated={handlePagesCreated}
      />

      {seedOpen && selectedProjectId && selectedTopic && (
        <SeedFromRoom
          projectId={selectedProjectId}
          topicId={selectedTopic.id}
          topicName={selectedTopic.name}
          onClose={() => setSeedOpen(false)}
          onSeeded={() => {
            // The list is stale the moment terms land on the node.
            fetchKeywords(1, false);
            triggerLinkedRefresh();
          }}
        />
      )}
    </div>
  );
}
